import { FunctionComponent, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

export interface LevelDialogProps {
  overlayColor: string;
  onOutsideClick: () => void;
  zIndex?: number;
  tacticTitle?: string;
  tacticDescription?: string;
}

const PortalPopupContainer = styled.div<{ zIndex: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${props => props.zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PopupOverlay = styled.div<{ overlayColor: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.overlayColor};
`;

const PopupContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 90%;
  margin: 20px;
`;

const CloseIconButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 2;
  outline: none;
  width: auto;
  height: auto;
  box-shadow: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: transparent;
  }
`;

const CloseIcon = styled.div`
  position: relative;
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 16px;
    height: 2px;
    background-color: #666;
    transition: background-color 0.2s ease;
  }

  &:before {
    transform: rotate(45deg);
  }

  &:after {
    transform: rotate(-45deg);
  }

  ${CloseIconButton}:hover & {
    transform: scale(1.2);
  }

  ${CloseIconButton}:hover &:before,
  ${CloseIconButton}:hover &:after {
    background-color: #333;
  }
`;

const ModalRoot = styled.div`
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  max-height: 85vh;
`;

const HeaderModalRoot = styled.div`
  padding: 16px 24px;
  background-color: #fff;
  border-radius: 8px 8px 0 0;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  font-family: Montserrat, sans-serif;
  color: #1b1a1c;
`;

const DescriptionContainer = styled.div`
  padding: 16px 24px;
  line-height: 1.5;
  font-size: 16px;
  color: #1b1a1c;
  font-family: Lato, sans-serif;
  overflow-y: auto;
  flex: 1;
`;

const FooterModalRoot = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  background-color: #fff;
  border-radius: 0 0 8px 8px;
`;

const ButtonPrimary = styled.button`
  border: none;
  border-radius: 8px;
  background-color: #00779f;
  color: #fff;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  padding: 8px 16px;
  cursor: pointer;
  font-family: Lato, sans-serif;
  transition: all 0.2s ease;

  &:hover {
    background-color: #005d7a;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const LevelDialog: FunctionComponent<LevelDialogProps> = ({
  overlayColor,
  onOutsideClick,
  zIndex = 9999,
  tacticTitle = 'Level 1',
  tacticDescription = 'Local leaders have a comprehensive overview of the quality of services available throughout the community and the needs that are not being met.',
}) => {
  // Create a ref to store the previous overflow style
  const previousOverflowRef = useRef<string>('');

  // Create a portal element
  const portalElement = useMemo(() => {
    return document.createElement('div');
  }, []);

  // Convert title to Sentence Case
  const formatTitle = (title: string) => {
    // If title is all uppercase, convert to Sentence Case
    if (title === title.toUpperCase()) {
      return title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
    }
    return title;
  };

  useEffect(() => {
    // Add the portal element to the body
    document.body.appendChild(portalElement);

    // Store the current overflow style and set it to hidden
    previousOverflowRef.current = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';

    // Cleanup on unmount
    return () => {
      // Remove the portal element
      document.body.removeChild(portalElement);

      // Restore the previous overflow style
      document.body.style.overflow = previousOverflowRef.current;
    };
  }, [portalElement]);

  useEffect(() => {
    // Handle escape key press
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOutsideClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onOutsideClick]);

  const content = (
    <PortalPopupContainer zIndex={zIndex}>
      <PopupOverlay overlayColor={overlayColor} onClick={onOutsideClick} />
      <PopupContentWrapper>
        <ModalRoot>
          <HeaderModalRoot>
            <HeaderTitle>{formatTitle(tacticTitle)}</HeaderTitle>
          </HeaderModalRoot>
          <DescriptionContainer>{tacticDescription}</DescriptionContainer>
          <FooterModalRoot>
            <ButtonPrimary onClick={onOutsideClick}>Got it</ButtonPrimary>
          </FooterModalRoot>
        </ModalRoot>
        <CloseIconButton onClick={onOutsideClick} aria-label="Close">
          <CloseIcon />
        </CloseIconButton>
      </PopupContentWrapper>
    </PortalPopupContainer>
  );

  return createPortal(content, portalElement);
};

export default LevelDialog;
